/**
 * ASCII Hero Engine - AsciiTimeline Class
 * Manages scene sequences, timed cues, dynamic illustration management,
 * and smooth crossfading between portrait pictures and configuration presets.
 */

export class AsciiTimeline {
  constructor(options = {}) {
    this.engine = options.engine || null;
    this.scenes = options.scenes || [];
    this.loop = options.loop !== undefined ? options.loop : true;
    this.autoPlay = options.autoPlay !== undefined ? options.autoPlay : true;
    this.defaultDuration = options.defaultDuration || 6500; // ms per scene
    this.defaultTransition = options.defaultTransition || 1200; // ms crossfade

    this.currentIndex = 0;
    this.isPlaying = this.autoPlay;
    this.elapsedInScene = 0;
    this.totalElapsed = 0;
    this.lastTime = typeof performance !== "undefined" ? performance.now() : Date.now();

    // Time-based cue points: [{ time: 3000, action: (engine, timeline) => {}, executed: false }]
    this.cuePoints = (options.cuePoints || []).map((cue) => Object.assign({ executed: false }, cue));

    // Callbacks
    this.onSceneChange = options.onSceneChange || null;
    this.onTimeUpdate = options.onTimeUpdate || null;
    this.onPlayStateChange = options.onPlayStateChange || null;

    this.controlsContainer = null;
    this.controlsElements = null;
  }

  attach(engine) {
    this.engine = engine;
    this.engine.timeline = this;
    if (this.scenes.length > 0) {
      this.goToScene(this.currentIndex, false);
    }
    return this;
  }

  addScene(scene) {
    this.scenes.push(scene);
    this._updateControlsUi();
    return this;
  }

  addCuePoint(timeMs, action) {
    this.cuePoints.push({ time: timeMs, action: action, executed: false });
    return this;
  }

  play() {
    this.isPlaying = true;
    this.lastTime = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (this.onPlayStateChange) this.onPlayStateChange(true);
    this._updateControlsUi();
  }

  pause() {
    this.isPlaying = false;
    if (this.onPlayStateChange) this.onPlayStateChange(false);
    this._updateControlsUi();
  }

  togglePlay() {
    if (this.isPlaying) this.pause();
    else this.play();
  }

  next() {
    if (this.scenes.length === 0) return;
    let nextIdx = this.currentIndex + 1;
    if (nextIdx >= this.scenes.length) {
      if (this.loop) nextIdx = 0;
      else {
        this.pause();
        return;
      }
    }
    this.goToScene(nextIdx, true);
  }

  prev() {
    if (this.scenes.length === 0) return;
    let prevIdx = this.currentIndex - 1;
    if (prevIdx < 0) {
      if (this.loop) prevIdx = this.scenes.length - 1;
      else prevIdx = 0;
    }
    this.goToScene(prevIdx, true);
  }

  goToScene(indexOrId, transition = true) {
    let targetIndex = -1;
    if (typeof indexOrId === "number") {
      targetIndex = indexOrId;
    } else if (typeof indexOrId === "string") {
      targetIndex = this.scenes.findIndex((s) => s.id === indexOrId || s.name === indexOrId || s.title === indexOrId);
    }

    if (targetIndex < 0 || targetIndex >= this.scenes.length) return;

    this.currentIndex = targetIndex;
    this.elapsedInScene = 0;
    const scene = this.scenes[this.currentIndex];
    const transDuration = transition
      ? scene.transitionDuration !== undefined
        ? scene.transitionDuration
        : this.defaultTransition
      : 0;

    if (this.engine) {
      this.engine.applyScene(scene, transDuration);
    }

    if (this.onSceneChange) {
      this.onSceneChange(scene, this.currentIndex, this);
    }

    this._updateControlsUi();
  }

  update(now = typeof performance !== "undefined" ? performance.now() : Date.now()) {
    const dt = now - this.lastTime;
    this.lastTime = now;

    if (!this.isPlaying || this.scenes.length === 0) return;

    this.elapsedInScene += dt;
    this.totalElapsed += dt;

    const currentScene = this.scenes[this.currentIndex];
    const sceneDuration = currentScene.duration || this.defaultDuration;

    // Check time-based cues
    for (let i = 0; i < this.cuePoints.length; i++) {
      const cue = this.cuePoints[i];
      if (!cue.executed && this.totalElapsed >= cue.time) {
        cue.executed = true;
        if (typeof cue.action === "function") {
          cue.action(this.engine, this);
        }
      }
    }

    const progress = Math.min(1.0, this.elapsedInScene / sceneDuration);

    if (this.onTimeUpdate) {
      this.onTimeUpdate({
        sceneIndex: this.currentIndex,
        scene: currentScene,
        sceneElapsed: this.elapsedInScene,
        sceneDuration: sceneDuration,
        sceneProgress: progress,
        totalElapsed: this.totalElapsed,
        isPlaying: this.isPlaying,
      });
    }

    if (this.controlsElements && this.controlsElements.progressBar) {
      this.controlsElements.progressBar.style.width = (progress * 100).toFixed(1) + "%";
    }

    // Advance to next scene when duration expires
    if (this.elapsedInScene >= sceneDuration) {
      this.next();
    }
  }

  /**
   * Mount a minimalist ASCII timeline controller UI (<, O / X, >)
   */
  mountControls(container = typeof document !== "undefined" ? document.body : null) {
    if (typeof document === "undefined") return this;
    if (typeof container === "string") {
      container = document.querySelector(container);
    }
    if (!container) return this;

    // Remove any existing controls
    const existing = document.getElementById("ascii-timeline-controller");
    if (existing) existing.remove();

    const wrapper = document.createElement("div");
    wrapper.id = "ascii-timeline-controller";
    wrapper.className = "ascii-timeline-controller";

    wrapper.innerHTML = `
      <button id="tl-prev-btn" class="tl-nav-btn" aria-label="Previous Slide">&lt;</button>
      <div id="tl-dots-container" class="tl-dots-container"></div>
      <button id="tl-next-btn" class="tl-nav-btn" aria-label="Next Slide">&gt;</button>
    `;

    container.appendChild(wrapper);

    this.controlsContainer = wrapper;
    this.controlsElements = {
      prevBtn: wrapper.querySelector("#tl-prev-btn"),
      nextBtn: wrapper.querySelector("#tl-next-btn"),
      dotsContainer: wrapper.querySelector("#tl-dots-container"),
    };

    // Event listeners
    this.controlsElements.prevBtn.onclick = () => this.prev();
    this.controlsElements.nextBtn.onclick = () => this.next();

    this._updateControlsUi();
    return this;
  }

  _updateControlsUi() {
    if (!this.controlsElements || !this.controlsElements.dotsContainer) return;

    const currentScene = this.scenes[this.currentIndex];
    const accentColor =
      currentScene && (currentScene.accentColor || (currentScene.config && currentScene.config.midGreyColor))
        ? currentScene.accentColor || currentScene.config.midGreyColor
        : "#ff7120";

    const dots = this.controlsElements.dotsContainer;
    dots.innerHTML = "";
    for (let i = 0; i < this.scenes.length; i++) {
      const isCurrent = i === this.currentIndex;
      const dot = document.createElement("span");
      dot.textContent = isCurrent ? "X" : "O";
      dot.className = isCurrent ? "tl-dot active" : "tl-dot";
      dot.style.setProperty("color", isCurrent ? accentColor : "rgba(255, 255, 255, 0.35)", "important");
      dot.style.textShadow = isCurrent ? `0 0 10px ${accentColor}` : "none";

      dot.onclick = () => this.goToScene(i, true);
      dots.appendChild(dot);
    }
  }
}
